import { useState } from 'react'
import { FileArchive, FileDown, Download } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { FileUploader } from '@/components/tools/file-uploader'
import { FileInfo } from '@/components/tools/file-info'
import { ProgressBar } from '@/components/tools/progress-bar'
import { ErrorMessage } from '@/components/tools/error-message'
import { useToolStore } from '@/stores/tool-store'
import { downloadBlob, basename } from '@/lib/download'
import { formatBytes } from '@/lib/format'
import { extractZip, type ZipFileEntry } from './lib'
import { useI18n } from '@/lib/i18n'

const MAX_ZIP_MB = 512

const MIME_BY_EXT: Record<string, string> = {
  png: 'image/png',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  gif: 'image/gif',
  webp: 'image/webp',
  svg: 'image/svg+xml',
  mp3: 'audio/mpeg',
  wav: 'audio/wav',
  mp4: 'video/mp4',
  webm: 'video/webm',
  txt: 'text/plain',
  json: 'application/json',
  pdf: 'application/pdf',
}

function mimeFor(name: string): string {
  const ext = name.split('.').pop()?.toLowerCase() ?? ''
  return MIME_BY_EXT[ext] ?? 'application/octet-stream'
}

/** ZIP extraction panel: archive → entry list → per-file downloads. */
export function ExtractPanel() {
  const [zipFile, setZipFile] = useState<File | null>(null)
  const [entries, setEntries] = useState<ZipFileEntry[]>([])
  const { status, progress, error, selectFile, start, setProgress, finish, fail, reset } =
    useToolStore()
  const { t } = useI18n()

  const handleFile = (file: File) => {
    setZipFile(file)
    setEntries([])
    selectFile()
  }

  const handleExtract = async () => {
    if (!zipFile) return
    start()
    try {
      const result = await extractZip(zipFile, setProgress)
      setEntries(result)
      finish(new Blob([], { type: 'application/zip' }), zipFile.name)
    } catch (err) {
      console.error('ZIP extraction failed:', err)
      fail(t('extFailed'))
    }
  }

  const handleReset = () => {
    setZipFile(null)
    setEntries([])
    reset()
  }

  if (status === 'idle') {
    return <FileUploader accept={['.zip']} maxSizeMB={MAX_ZIP_MB} onFileSelect={handleFile} />
  }

  if (status === 'processing') {
    return (
      <div className="rounded-xl border bg-card p-6 shadow-sm">
        <div className="mb-4 flex items-center gap-2">
          <FileArchive className="h-5 w-5 text-primary" aria-hidden />
          <p className="font-medium">{t('extExtracting')}</p>
        </div>
        <ProgressBar value={progress} label={t('extProgress')} />
      </div>
    )
  }

  if (status === 'done') {
    return (
      <div className="space-y-4">
        <div className="rounded-xl border bg-card p-6 shadow-sm">
          <div className="flex items-center gap-2">
            <FileArchive className="h-5 w-5 text-primary" aria-hidden />
            <p className="font-medium">
              {entries.length === 1
                ? t('extExtractedOne', { n: entries.length })
                : t('extExtractedMany', { n: entries.length })}
            </p>
          </div>

          <div className="mt-4 max-h-72 space-y-1 overflow-y-auto">
            {entries.map((entry) => (
              <div
                key={entry.name}
                className="flex items-center justify-between gap-3 rounded-lg px-3 py-2 hover:bg-muted"
              >
                <p className="truncate text-sm">{entry.name}</p>
                <div className="flex shrink-0 items-center gap-3">
                  <span className="font-mono text-xs text-muted-foreground">
                    {formatBytes(entry.size)}
                  </span>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() =>
                      downloadBlob(new Blob([entry.data], { type: mimeFor(entry.name) }), basename(entry.name))
                    }
                  >
                    <FileDown className="mr-1" aria-hidden />
                    {t('extSave')}
                  </Button>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-4 flex flex-col gap-2 sm:flex-row">
            <Button
              variant="outline"
              className="flex-1"
              onClick={() => {
                // Browsers may block multiple automatic downloads; users can
                // click per-file Save if this is throttled.
                for (const entry of entries) {
                  downloadBlob(new Blob([entry.data], { type: mimeFor(entry.name) }), basename(entry.name))
                }
              }}
            >
              <Download className="mr-1" aria-hidden />
              {t('extDownloadAll')}
            </Button>
            <Button variant="outline" className="flex-1" onClick={handleReset}>
              {t('extNewArchive')}
            </Button>
          </div>
        </div>
      </div>
    )
  }

  if (status === 'error') {
    return (
      <div className="space-y-4">
        <ErrorMessage
          message={error ?? t('commonErrorDefault')}
          onRetry={zipFile ? handleExtract : undefined}
        />
        {zipFile && <FileInfo file={zipFile} onRemove={handleReset} />}
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {zipFile && <FileInfo file={zipFile} onRemove={handleReset} />}
      <Button onClick={handleExtract} className="w-full" size="lg">
        <FileArchive className="mr-1" aria-hidden />
        {t('extAction')}
      </Button>
    </div>
  )
}
